/**
 * 🔒 PAGE POLITIQUE DE CONFIDENTIALITÉ
 * Pour La Petite Épicerie (site principal)
 */

import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Lock, Database, Eye, Trash2, Shield, Mail, Cookie } from 'lucide-react';

const PrivacyPolicyPage = ({ onBack, isDarkMode = true }) => {
  const sections = [
    {
      title: "1. Responsable du traitement",
      icon: <Shield className="w-5 h-5" />,
      content: `**VM MARKET** (La Petite Épicerie)
138 Rue Arthur Lamendin
62980 Vermelles, France

SIRET : 428 992 796 00022
Email : contact@la-petite-epicerie.shop
Téléphone : 07 80 64 89 76

Nous sommes responsables du traitement de vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD).`
    },
    {
      title: "2. Données collectées",
      icon: <Database className="w-5 h-5" />,
      content: `**Données d'identification** :
- Nom, prénom
- Adresse email
- Numéro de téléphone
- Adresse de livraison

**Données de commande** :
- Historique des achats
- Préférences de livraison
- Mode de paiement utilisé (sans données bancaires)

**Données de fidélité** :
- Nombre de tampons
- Avantages utilisés
- Cartes de fidélité actives

**Données techniques** :
- Adresse IP
- Type d'appareil et navigateur
- Données de navigation`
    },
    {
      title: "3. Finalités du traitement",
      content: `Vos données sont utilisées pour :

✅ **Gestion des commandes** : Traitement, livraison, facturation
✅ **Programme de fidélité** : Suivi des tampons, attribution des avantages
✅ **Communication** : Informations sur vos commandes, promotions (avec consentement)
✅ **Amélioration du service** : Statistiques anonymes d'utilisation
✅ **Obligations légales** : Conservation des factures, réponse aux réquisitions

❌ Vos données ne sont JAMAIS vendues à des tiers.`
    },
    {
      title: "4. Base légale",
      content: `Nos traitements reposent sur :

- **L'exécution du contrat** : Pour traiter vos commandes et gérer votre compte fidélité
- **Votre consentement** : Pour l'envoi de communications marketing
- **Notre intérêt légitime** : Pour améliorer nos services et assurer la sécurité
- **Obligations légales** : Pour la conservation des données fiscales`
    },
    {
      title: "5. Durée de conservation",
      content: `⏱️ **Compte client actif** : Données conservées pendant toute la durée de la relation commerciale

🗑️ **Compte inactif** : Données supprimées après 3 ans d'inactivité

💳 **Données de facturation** : 10 ans (obligation légale)

📊 **Logs techniques** : 12 mois maximum

🍪 **Cookies** : Selon leur type (voir section dédiée)`
    },
    {
      title: "6. Vos droits RGPD",
      icon: <Eye className="w-5 h-5" />,
      content: `Conformément au RGPD, vous disposez des droits suivants :

📋 **Droit d'accès** : Obtenir une copie de vos données
✏️ **Droit de rectification** : Corriger vos données inexactes
🗑️ **Droit à l'effacement** : Supprimer vos données
⏸️ **Droit à la limitation** : Suspendre le traitement
📦 **Droit à la portabilité** : Récupérer vos données
🚫 **Droit d'opposition** : Vous opposer à certains traitements
↩️ **Droit de retrait du consentement** : À tout moment

Pour exercer ces droits : contact@la-petite-epicerie.shop

Délai de réponse : 30 jours maximum.`
    },
    {
      title: "7. Sécurité des données",
      icon: <Lock className="w-5 h-5" />,
      content: `Nous mettons en œuvre des mesures de sécurité appropriées :

🔐 Chiffrement des données sensibles
🔒 Connexions sécurisées (HTTPS)
🛡️ Protection contre les accès non autorisés
📊 Accès limité aux données (principe du moindre privilège)

En cas de violation de données, vous serez informé dans les 72 heures si un risque existe pour vos droits.`
    },
    {
      title: "8. Partage des données",
      content: `Vos données peuvent être partagées avec :

**Prestataires de livraison** : Nom, adresse, téléphone (pour la livraison uniquement)
**Prestataires de paiement** : Données nécessaires au traitement du paiement
**Hébergeur** : Stockage sécurisé des données
**Autorités** : Sur demande légale uniquement

🚫 Aucun transfert hors UE sans garanties appropriées.
🚫 Aucune vente de données à des tiers.`
    },
    {
      title: "9. Cookies",
      icon: <Cookie className="w-5 h-5" />,
      content: `Nous utilisons des cookies pour :

**Essentiels** (toujours actifs) :
- Session et authentification
- Panier d'achat
- Sécurité

**Fonctionnels** (avec consentement) :
- Mémorisation des préférences
- Dernière localisation de livraison

**Analytiques** (avec consentement) :
- Statistiques d'utilisation anonymes

Vous pouvez gérer vos préférences de cookies dans les paramètres de votre navigateur.`
    },
    {
      title: "10. Modifications",
      content: `Cette politique peut être modifiée. En cas de changement substantiel, vous serez informé par email ou notification sur le site.

La date de dernière mise à jour est indiquée en haut de ce document.`
    },
    {
      title: "11. Réclamation",
      content: `Si vous estimez que vos droits ne sont pas respectés, vous pouvez :

1. Nous contacter : contact@la-petite-epicerie.shop
2. Saisir la CNIL : www.cnil.fr

Commission Nationale de l'Informatique et des Libertés
3 Place de Fontenoy, TSA 80715
75334 Paris Cedex 07`
    }
  ];

  return (
    <div className={`min-h-screen overflow-x-hidden overflow-y-auto ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-10 ${isDarkMode ? 'bg-gray-900/95' : 'bg-white/95'} backdrop-blur-sm border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          {onBack && (
            <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div className="flex items-center gap-2">
            <Lock className="w-6 h-6 text-green-400" />
            <h1 className="text-xl font-bold">Politique de Confidentialité</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-6 rounded-2xl mb-8 ${isDarkMode ? 'bg-green-900/30 border border-green-500/30' : 'bg-green-50 border border-green-200'}`}
        >
          <div className="flex items-center gap-3 mb-3">
            <span className="text-4xl">🛒</span>
            <div>
              <h2 className="font-bold text-lg">La Petite Épicerie - Protection de vos données</h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Dernière mise à jour : Janvier 2026
              </p>
            </div>
          </div>
          <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
            Votre vie privée est importante pour nous. Cette politique explique comment nous collectons, 
            utilisons et protégeons vos données personnelles conformément au RGPD.
          </p>
        </motion.div>

        {sections.map((section, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            className={`mb-6 p-6 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-white'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
          >
            <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
              {section.icon && <span className="text-green-400">{section.icon}</span>}
              {section.title}
            </h3>
            <div className={`whitespace-pre-line ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {section.content}
            </div>
          </motion.div>
        ))}

        {/* Footer */}
        <div className={`text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          <p className="text-sm">© 2026 La Petite Épicerie - VM MARKET. Tous droits réservés.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
